import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressMembersApiMembersUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/members/v1/members',
        destPath: '/v1/members',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/members-ng-api',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/members/v1/members',
        destPath: '/v1/members',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/members/v1/members',
        destPath: '/v1/members',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/members/v1/members',
        destPath: '/v1/members',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/members/v1/members',
        destPath: '/v1/members',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/members/v1/members',
        destPath: '/v1/members',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/members/v1/members',
        destPath: '/v1/members',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/members/v1/members',
        destPath: '/v1/members',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/_api/members/v1/members',
        destPath: '/v1/members',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/members/v1/members',
        destPath: '/v1/members',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/members/v1/members',
        destPath: '/v1/members',
      },
    ],
    _: [
      {
        srcPath: '/_api/members/v1/members',
        destPath: '/v1/members',
      },
    ],
    'members.wixapps.net': [
      {
        srcPath: '/_api/members/v1/members',
        destPath: '/v1/members',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_members_members';

/**
 * Updates the currently logged in member's slug.
 *
 * The `slug` is the end of a member's URL that refers to a specific logged-in member. For example, if a member's URL is `https://example.com/member/{my-member-slug}`, the slug is `my-member-slug`. The slug is case-sensitive and is generally derived from the member's `nickname`; otherwise, it's derived from the `loginEmail`.
 *
 * > **Note:**
 * > Only logged-in members can call this method without elevated permissions.
 * > To call this method as a different identity, [elevated permissions](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/about-elevated-permissions) are required.
 */
export function updateMySlug(payload: object): RequestOptionsFactory<any> {
  function __updateMySlug({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.members.api.Members.UpdateMySlug',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/my/slug',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'member.createdDate' },
              { path: 'member.updatedDate' },
              { path: 'member.lastLoginDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateMySlug;
}

/**
 * Updates a member's slug.
 *
 * To update the current member's slug, call [Update My Slug](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/update-my-slug).
 */
export function updateMemberSlug(payload: object): RequestOptionsFactory<any> {
  function __updateMemberSlug({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.members.api.Members.UpdateMemberSlug',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/{id}/slug',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'member.createdDate' },
              { path: 'member.updatedDate' },
              { path: 'member.lastLoginDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateMemberSlug;
}

/**
 * Joins the current member to the site community and sets their profile to public.
 *
 * When a member's profile is public, they have access to the site's
 * [Members Area](https://support.wix.com/en/article/site-members-about-the-members-area)
 * features, such as chat, forum, and followers,
 * and their profile is visible to other members and site visitors.
 *
 * > **Note:**
 * > Only logged-in members can call this method without elevated permissions.
 * > To call this method as a different identity, [elevated permissions](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/about-elevated-permissions) are required.
 */
export function joinCommunity(payload: object): RequestOptionsFactory<any> {
  function __joinCommunity({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.members.api.Members.JoinCommunity',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/join-community',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'member.createdDate' },
              { path: 'member.updatedDate' },
              { path: 'member.lastLoginDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __joinCommunity;
}

/**
 * Removes the current member from the site community and sets their profile to private.
 *
 * When a member's profile is private,
 * they do not have access to the site's
 * [Members Area](https://support.wix.com/en/article/site-members-about-the-members-area)
 * features, such as chat, forum, and followers,
 * and their profile is hidden from other members and site visitors.
 *
 * > **Notes:**
 * > + If a member leaves the site's community, their content, such as forum posts and blog comments, remain publicly visible.
 *
 * > + Only logged-in members can call this method without elevated permissions.
 * > + To call this method as a different identity, [elevated permissions](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/about-elevated-permissions) are required.
 */
export function leaveCommunity(payload: object): RequestOptionsFactory<any> {
  function __leaveCommunity({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.members.api.Members.LeaveCommunity',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/leave-community',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'member.createdDate' },
              { path: 'member.updatedDate' },
              { path: 'member.lastLoginDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __leaveCommunity;
}

/**
 * Retrieves the current member.
 *
 * >**Note:**
 * >This method requires [visitor or member authentication](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).
 *
 * To retrieve a member by `ID`, call [Get Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/get-member).
 */
export function getMyMember(payload: object): RequestOptionsFactory<any> {
  function __getMyMember({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.members.api.Members.GetMyMember',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/my',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'member.createdDate' },
              { path: 'member.updatedDate' },
              { path: 'member.lastLoginDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getMyMember;
}

/**
 * Retrieves a member by ID.
 *
 * >**Note:** The returned Member object contains only the fields that were explicitly added to the Member object. Custom Contact fields are **not** automatically added to the Member object. They must be [added to the Member object by the site owner](https://support.wix.com/en/article/site-members-customizing-your-member-profile-fields).
 *
 */
export function getMember(payload: object): RequestOptionsFactory<any> {
  function __getMember({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.members.api.Members.GetMember',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'member.createdDate' },
              { path: 'member.updatedDate' },
              { path: 'member.lastLoginDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getMember;
}

/**
 * Lists site members, given the provided paging and fieldsets.
 *
 * A member's default privacy status is `"PRIVATE"`. Private members aren't returned in the response if a [site visitor](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities#site-visitor) or [site member](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities#site-member) identity calls this method. Learn more about [managing your member privacy settings](https://support.wix.com/en/article/site-members-managing-your-member-privacy-settings).
 */
export function listMembers(payload: object): RequestOptionsFactory<any> {
  function __listMembers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.members.api.Members.ListMembers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'members.createdDate' },
              { path: 'members.updatedDate' },
              { path: 'members.lastLoginDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listMembers;
}

/**
 * Retrieves a list of up to 100 members, given the specified filters, fieldsets, sorting and paging, and returns a `MembersQueryBuilder` object.
 *
 * The returned object contains the query definition which is typically used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `MembersQueryBuilder` methods onto the query. `MembersQueryBuilder` methods enable you to sort, filter, and control the results that `queryMembers()` returns. The methods that are chained to `queryMembers()` are applied in the order they are called.
 *
 * `queryMembers()` runs with the following `MembersQueryBuilder` defaults that you can override:
 * - `skip`: `0`
 * - `limit`: `100`
 *
 * Currently supported fields for sorting:
 * - `profile.nickname`
 * - `contact.firstName`
 * - `contact.lastName`
 * - `createdDate`
 * - `lastLoginDate`
 *
 * The following `MembersQueryBuilder` methods are supported for the `queryMembers()` method. For a full description of the Locations object, see the returned for the `items` property in `MembersQueryResult`.
 */
export function queryMembers(payload: object): RequestOptionsFactory<any> {
  function __queryMembers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.members.api.Members.QueryMembers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'members.createdDate' },
              { path: 'members.updatedDate' },
              { path: 'members.lastLoginDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryMembers;
}

/**
 * Mutes a member.
 *
 * Muted members can't engage with a community. For example, they can't leave comments, like posts or comments, or share content.
 *
 * You can mute members in [Wix Blog](https://dev.wix.com/docs/rest/business-solutions/blog/introduction) and [Wix Forum](https://dev.wix.com/docs/rest/business-solutions/forum/introduction).
 *
 * To unmute a member, call [Unmute Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/unmute-member).
 */
export function muteMember(payload: object): RequestOptionsFactory<any> {
  function __muteMember({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.members.api.Members.MuteMember',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/{id}/mute',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'member.createdDate' },
              { path: 'member.updatedDate' },
              { path: 'member.lastLoginDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __muteMember;
}

/**
 * Unmutes a member.
 *
 * Unmuted members can engage with the community. For example, leave comments, like posts or comments, and share content.
 *
 * You can unmute members in [Wix Blog](https://dev.wix.com/docs/rest/business-solutions/blog/introduction) and [Wix Forum](https://dev.wix.com/docs/rest/business-solutions/forum/introduction).
 *
 * To mute a member, call [Mute Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/mute-member).
 */
export function unmuteMember(payload: object): RequestOptionsFactory<any> {
  function __unmuteMember({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.members.api.Members.UnmuteMember',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/{id}/unmute',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'member.createdDate' },
              { path: 'member.updatedDate' },
              { path: 'member.lastLoginDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __unmuteMember;
}

/**
 * Changes member status to `"APPROVED"` and gives access to members-only pages.
 *
 * Call this API to:
 * - Approve a pending member.
 * - Unblock a blocked member.
 *
 * To approve multiple members, call [Bulk Approve Members](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/bulk-approve-members).
 *
 * To block a member, call [Block Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/block-member).
 */
export function approveMember(payload: object): RequestOptionsFactory<any> {
  function __approveMember({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.members.api.Members.ApproveMember',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/{id}/approve',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'member.createdDate' },
              { path: 'member.updatedDate' },
              { path: 'member.lastLoginDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __approveMember;
}

/**
 * Blocks a member.
 *
 * A blocked member can't log in to members-only pages.
 *
 * To block multiple members, call [Bulk Block Members](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/bulk-block-members).
 *
 * To unblock a member, call [Approve Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/approve-member).
 */
export function blockMember(payload: object): RequestOptionsFactory<any> {
  function __blockMember({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.members.api.Members.BlockMember',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/{id}/block',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'member.createdDate' },
              { path: 'member.updatedDate' },
              { path: 'member.lastLoginDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __blockMember;
}

/**
 * Disconnects a member.
 *
 * A disconnected member can't log in to members-only pages, isn't visible in the dashboard,
 * and their `status` changes to `OFFLINE`.
 *
 * Use this when you want to permanently remove access without deleting member data.
 *
 * >**Note:** This action is irreversible. To connect the same member again, you have to create a member with a new slug.
 */
export function disconnectMember(payload: object): RequestOptionsFactory<any> {
  function __disconnectMember({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.members.api.Members.DisconnectMember',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/{id}/disconnect',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'member.createdDate' },
              { path: 'member.updatedDate' },
              { path: 'member.lastLoginDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __disconnectMember;
}

/**
 * Deletes a member by ID.
 *
 * All content created by this member is transferred to a site owner's account. For example, if a Wix user had blog posts, those posts are transferred to the site owner's account, which then becomes the owner of those posts.
 *
 * To delete the current member, call [Delete My Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/delete-my-member).
 *
 * To delete multiple members by `memberId`, call [Bulk Delete Members](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/bulk-delete-members).
 *
 * To delete multiple members by a specified filter, call [Bulk Delete Members By Filter](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/bulk-delete-members-by-filter).
 */
export function deleteMember(payload: object): RequestOptionsFactory<any> {
  function __deleteMember({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'DELETE' as any,
      methodFqn: 'com.wixpress.members.api.Members.DeleteMember',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteMember;
}

/**
 * Deletes the current member.
 *
 * After calling this method, the member is logged out of the site.
 *
 * All content created by this member is transferred to another account. For example, if a Wix user had blog posts, those posts are transferred to the account specified in `contentAssignedId`, which then becomes the owner of those posts.
 *
 * >**Note:**
 * >This method requires [visitor or member authentication](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).
 *
 * To delete a member by `ID`, call [Delete Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/delete-member).
 *
 * To delete multiple members by `memberId`, call [Bulk Delete Members](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/bulk-delete-members).
 *
 * To delete multiple members by a specified filter, call [Bulk Delete Members By Filter](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/bulk-delete-members-by-filter).
 */
export function deleteMyMember(payload: object): RequestOptionsFactory<any> {
  function __deleteMyMember({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'DELETE' as any,
      methodFqn: 'com.wixpress.members.api.Members.DeleteMyMember',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/my',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteMyMember;
}

/**
 * Deletes multiple members by `memberId`.
 *
 * All content created by these members is transferred to a site owner's account. For example, if Wix users had blog posts, those posts are transferred to the site owner's account, which then becomes the owner of those posts.
 *
 * To delete only the current member, call [Delete My Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/delete-my-member).
 *
 * To delete a single member by `ID`, call [Delete Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/delete-member).
 *
 * To delete multiple members by a specified filter, call [Bulk Delete Members By Filter](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/bulk-delete-members-by-filter).
 */
export function bulkDeleteMembers(payload: object): RequestOptionsFactory<any> {
  function __bulkDeleteMembers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.members.api.Members.BulkDeleteMembers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/bulk/delete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteMembers;
}

/**
 * Deletes multiple members by the specified filter.
 *
 * All content created by these members is transferred to a site owner's account. For example, if Wix users had blog posts, those posts are transferred to the site owner's account, which then becomes the owner of those posts.
 *
 * To delete only the current member, call [Delete My Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/delete-my-member).
 *
 * To delete a single member by `ID`, call [Delete Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/delete-member).
 *
 * To delete multiple members by `memberId`, call [Bulk Delete Members](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/bulk-delete-members).
 */
export function bulkDeleteMembersByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteMembersByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.members.api.Members.BulkDeleteMembersByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/bulk/delete-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteMembersByFilter;
}

/**
 * Changes status of multiple members to `"APPROVED"`, and gives access to members-only pages.
 *
 * Call this API to:
 * - Approve pending members.
 * - Unblock blocked members.
 *
 * To approve a single member, call [Approve Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/approve-member).
 */
export function bulkApproveMembers(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkApproveMembers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.members.api.Members.BulkApproveMembers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/bulk/approve-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkApproveMembers;
}

/**
 * Blocks multiple members by a specified filter.
 *
 * Blocked members can't log in to members-only pages.
 *
 * To block a single member, call [Block Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/block-member).
 *
 * To unblock a single member, call [Approve Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/approve-member).
 *
 * To unblock multiple members, call [Bulk Approve Members](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/bulk-approve-members).
 */
export function bulkBlockMembers(payload: object): RequestOptionsFactory<any> {
  function __bulkBlockMembers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.members.api.Members.BulkBlockMembers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/bulk/block-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkBlockMembers;
}

/**
 * Creates a site member.
 *
 * After creation, you can call
 * [Send Set Password Email](https://dev.wix.com/docs/rest/crm/members-contacts/members/member-authentication/send-set-password-email)
 * to email the member with a link to set their password.
 * The member can log in to the site
 * when they set their password for the first time.
 *
 * > **Note:**
 * > When creating multiple members,
 * > set your requests at least 1 second apart to keep below rate limits.
 */
export function createMember(payload: object): RequestOptionsFactory<any> {
  function __createMember({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'member.createdDate' },
          { path: 'member.updatedDate' },
          { path: 'member.lastLoginDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.members.api.Members.CreateMember',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'member.createdDate' },
              { path: 'member.updatedDate' },
              { path: 'member.lastLoginDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createMember;
}

/**
 * Updates a member's properties.
 *
 * Only the requested fields are updated.
 * To clear a field's value, set an empty value with an empty string `""`.
 *
 * > **Note:**
 * > Updating the `contact.addresses`, `contact.emails`, or `contact.phones` array overwrites the entire array, so any existing values you want to retain must be passed in the `updateMember()` call along with the new values to add.
 * > However, passing an empty array will have no effect, and these methods must be used to clear all data from the respective array:
 * >- To clear `contact.addresses`, use `deleteMemberAddresses()`.
 * >- To clear `contact.emails`, use `deleteMemberEmails()`.
 * >- To clear `contact.phones`, use `deleteMemberPhones()`.
 *
 * > **Note:**
 * > Only logged-in members can call this method without elevated permissions.
 * > To call this method as a different identity, [elevated permissions](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/about-elevated-permissions) are required.
 */
export function updateMember(payload: object): RequestOptionsFactory<any> {
  function __updateMember({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'member.createdDate' },
          { path: 'member.updatedDate' },
          { path: 'member.lastLoginDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'PATCH' as any,
      methodFqn: 'com.wixpress.members.api.Members.UpdateMember',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/{member.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'member.createdDate' },
              { path: 'member.updatedDate' },
              { path: 'member.lastLoginDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateMember;
}

/**
 * Clears a member's phone numbers.
 *
 * `deleteMemberPhones()` clears the `phones` array under the `contact` property.
 *
 * > **Note:**
 * > Only logged-in members can call this method without elevated permissions.
 * > To call this method as a different identity, [elevated permissions](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/about-elevated-permissions) are required.
 */
export function deleteMemberPhones(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteMemberPhones({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'DELETE' as any,
      methodFqn: 'com.wixpress.members.api.Members.DeleteMemberPhones',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/{id}/phones',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'member.createdDate' },
              { path: 'member.updatedDate' },
              { path: 'member.lastLoginDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __deleteMemberPhones;
}

/**
 * Clears a member's email addresses.
 *
 * `deleteMemberEmails()` clears the `emails` array under the `contact` property.
 *
 * > **Notes:**
 * > A member can still log in with their `loginEmail`,
 * > which is not cleared when this method is called.
 *
 * > Only logged-in members can call this method without elevated permissions.
 * > To call this method as a different identity, [elevated permissions](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/about-elevated-permissions) are required.
 */
export function deleteMemberEmails(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteMemberEmails({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'DELETE' as any,
      methodFqn: 'com.wixpress.members.api.Members.DeleteMemberEmails',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/{id}/emails',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'member.createdDate' },
              { path: 'member.updatedDate' },
              { path: 'member.lastLoginDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __deleteMemberEmails;
}

/**
 * Deletes a member's street addresses.
 *
 * `deleteMemberAddresses()` clears the `addresses` array under the `contact` property.
 *
 * > **Note:**
 * > Only logged-in members can call this method without elevated permissions.
 * > To call this method as a different identity, [elevated permissions](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/about-elevated-permissions) are required.
 */
export function deleteMemberAddresses(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteMemberAddresses({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member',
      method: 'DELETE' as any,
      methodFqn: 'com.wixpress.members.api.Members.DeleteMemberAddresses',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersApiMembersUrl({
        protoPath: '/v1/members/{id}/addresses',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'member.createdDate' },
              { path: 'member.updatedDate' },
              { path: 'member.lastLoginDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __deleteMemberAddresses;
}
